import { useEffect, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import PropTypes from 'prop-types';
import { setSpecifier, setSelString, setSurfaceList, setVizLoading, updateSceneSelString } from '../../../store/visualizerSlice';
import styles from './Visualizer.module.css';

export default function Visualizer({ specifier, sel }) {
    const dispatch = useDispatch();
    const vizLoading = useSelector((state) => state.visualizer.vizLoading);
    const activeScene = useSelector((state) => state.visualizer.activeScene);
    const componentRegistered = useRef(false);
    
    // Track the current specifier being rendered to avoid race conditions
    const currentSpecifierRef = useRef(specifier);
    const currentAreaRef = useRef(activeScene?.area);

    // Update refs when props/state change
    useEffect(() => {
        currentSpecifierRef.current = specifier;
        currentAreaRef.current = activeScene?.area;
    }, [specifier, activeScene?.area]);

    useEffect(() => {
        const chamViz = document.getElementById('chamViz');
        
        console.log('Visualizer useEffect - specifier:', specifier, 'sel:', sel);
        
        if (!chamViz) {
            console.error('cham-viz element not found');
            return;
        }

        // Event listener for when scene is ready
        const handleReady = (result) => {
            console.log('Chameleon viz ready for specifier:', currentSpecifierRef.current);
            const surfaceData = structuredClone(result.value);
            const saveData = chamViz.getSaveData();
            
            dispatch(setSpecifier(saveData.specifier));
            dispatch(setSurfaceList(surfaceData));
            dispatch(setVizLoading(false));
            
            console.log('Surface list loaded:', surfaceData.length, 'surfaces');
        };

        // Event listener for when product application starts
        const handleSurfaceApply = (result) => {
            console.log('Surface apply started');
            dispatch(setVizLoading(true));
        };

        // Event listener for when product is applied
        const handleSurfaceApplyDone = (result) => {
            const saveData = chamViz.getSaveData();
            const areaAtApply = currentAreaRef.current;
            
            console.log('Surface apply done for area:', areaAtApply);
            console.log('New sel string:', saveData.sel);
            
            // Only update if we have a valid area
            if (areaAtApply) {
                dispatch(setSelString(saveData.sel));
                // Use the new action that explicitly specifies the area
                dispatch(updateSceneSelString({ area: areaAtApply, sel: saveData.sel }));
            } else {
                console.warn('No area found when surface_apply_done fired');
            }
            
            dispatch(setVizLoading(false));
        };

        // Function to initialize the visualization
        const initViz = () => {
            if (!chamViz.load) {
                console.warn('cham-viz not fully loaded yet');
                return;
            }

            // Add event listeners
            chamViz.addEventListener('ready', handleReady);
            chamViz.addEventListener('surface_apply', handleSurfaceApply);
            chamViz.addEventListener('surface_apply_done', handleSurfaceApplyDone);

            // Disable mouseovers for performance
            chamViz.useMouseovers = false;

            // Load the scene
            if (specifier && sel && typeof specifier === 'string' && typeof sel === 'string') {
                console.log('Loading scene:', specifier, 'sel:', sel);
                dispatch(setVizLoading(true));
                chamViz.load(specifier, sel);
            } else {
                console.warn('Invalid specifier or sel:', { specifier, sel });
            }

            componentRegistered.current = true;
        };

        // Check if component is already registered
        if (chamViz.load) {
            initViz();
        } else {
            // Wait for component registration
            const handleComponentRegistered = (e) => {
                if (e.value === 'cham-viz' && !componentRegistered.current) {
                    console.log('cham-viz component registered');
                    initViz();
                }
            };

            document.addEventListener('componentregistered', handleComponentRegistered);

            // Cleanup registration listener
            return () => {
                document.removeEventListener('componentregistered', handleComponentRegistered);
            };
        }

        // Cleanup event listeners
        return () => {
            if (chamViz) {
                chamViz.removeEventListener('ready', handleReady);
                chamViz.removeEventListener('surface_apply', handleSurfaceApply);
                chamViz.removeEventListener('surface_apply_done', handleSurfaceApplyDone);
            }
        };
    }, [specifier, sel, dispatch]);

    return (
        <div className={`${styles.vizContainer} ${vizLoading ? styles.loading : ''}`}>
            <div className={styles.loadingScreen}>
                <div className={styles.loadingContent}>
                    <div className={styles.spinner}></div>
                    <span>Loading...</span>
                </div>
            </div>
            <cham-viz id="chamViz" spec="" width="1024" usewait="1" active="1"></cham-viz>
            <div className={styles.disclaimer}>
                <p>
                    <span>Disclaimer:</span> &nbsp;The images shown are conceptual renderings and may showcase upgrades and/or optional features that are not included in the standard base pricing. Additional costs may apply for these features. Please refer to your New Home Sales Consultant for standard and upgrade information.
                </p>
            </div>
        </div>
    );
}

Visualizer.propTypes = {
    specifier: PropTypes.string.isRequired,
    sel: PropTypes.string.isRequired
};
